import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Typography from 'aquilla/core/atoms/Typography';
import Button, { BounceLoader } from 'aquilla/core/molecules/Button';
import useFetch from '@hooks/useFetch';
import Dialog from '@comp/Dialog/Dialog';
import { PROGRAMME_LABEL_MAPPING } from '@constants/quiz';
import { emptyObject, noOp } from '@constants/empty';
import {
  getDateStringInTestSeriesHeader,
  getEducatorsString,
  getTestSeriesDetailsAPIKey
} from '@cont/SeeAll/utils';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import { HOST_TYPE } from '@utils/api-helper/host';
import { pluralize } from '@utils/textHelper';
import SubscriptionBenefits from 'aquilla/icons/unacademyStore/SubscriptionBenefits';
import SessionCancelled from 'aquilla/icons/unacademyStore/SessionCancelled';
import useSyllabusProgrammePanel from './useSyllabusProgrammePanel';

const PANEL_OUTER_WIDTH = '336px';

const Panel = styled.div`
  box-sizing: border-box;
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-self: flex-start;
  position: sticky;
  top: ${Spacings.SPACING_10B};
  width: ${PANEL_OUTER_WIDTH};
  min-width: ${PANEL_OUTER_WIDTH};
  margin: ${Spacings.SPACING_6B} 0;
  padding: ${Spacings.SPACING_4B};
  border: 1px solid #dde6ed;
  border-radius: 8px;
  background: var(--color-i-white);

  ${mediaQuery.uptoTablet} {
    position: sticky;
    top: auto;
    bottom: 0;
    z-index: 2;
    align-self: stretch;
    width: 100%;
    min-width: 0;
    margin: 0 0 ${Spacings.SPACING_4B};
    padding-bottom: calc(${Spacings.SPACING_4B} + env(safe-area-inset-bottom));
  }
`;

const InnerColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_4B};
  width: 100%;
`;

const TopSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_4B};
  width: 100%;
`;

const HeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_3B};
  width: 100%;
`;

const TypeLabel = styled(Typography)`
  color: #49bbde;
  font-size: 12px;
  line-height: 12px;
  font-weight: ${Fonts.HEAVY};
  letter-spacing: 0.24px;
  text-transform: uppercase;
`;

const Title = styled(Typography)`
  color: #3c4852;
  font-size: 20px;
  line-height: 26px;
  font-weight: ${Fonts.HEAVY};
  letter-spacing: -0.1px;
`;

const DetailsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_4B};
  width: 100%;
`;

const DetailRow = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_3B};
  align-items: ${({ $centerIcon }) => ($centerIcon ? 'center' : 'flex-start')};
  width: 100%;
`;

const EducatorIconCircle = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(45, 129, 247, 0.1);
`;

const ScheduleIconWrap = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
`;

const DetailText = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${Spacings.SPACING_1B};
  min-width: 0;
`;

const DetailLabel = styled(Typography)`
  margin: 0;
  color: #7a8b94;
  font-size: 12px;
  line-height: 16px;
`;

const DetailValue = styled(Typography)`
  margin: 0;
  color: #3c4852;
  font-size: 12px;
  line-height: 16px;
`;

const ActionButton = styled(Button)`
  width: 100%;
  height: ${Spacings.SPACING_12B};
  flex-shrink: 0;
`;

const PrimeActionButton = styled.button`
  width: 100%;
  height: ${Spacings.SPACING_12B};
  flex-shrink: 0;
  border: none;
  border-radius: 8px;
  background: #9875ff;
  color: #fff;
  font-size: 16px;
  line-height: 24px;
  font-weight: ${Fonts.HEAVY};
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SyllabusPracticePanel = ({ programmeUID, goalUID, goalSlug }) => {
  const { data: programmeDetails = emptyObject, isLoading } = useFetch(
    programmeUID ? getTestSeriesDetailsAPIKey(programmeUID) : null,
    { hostType: HOST_TYPE.TEST_SERIES },
    { revalidateOnMount: true }
  );

  const {
    name,
    programmeSubtype,
    authors = [],
    startsAt,
    endsAt,
    goal = {}
  } = programmeDetails;

  const resolvedGoalUID = goalUID || goal?.uid;
  const resolvedGoalSlug = goalSlug || goal?.slug;

  const {
    buttonLabel,
    buttonVariant,
    showPrimeTrialCta,
    isCtaLoading,
    handleCtaClick,
    isEnrollStateValidating,
    unEnrollModalOpen,
    onCloseUnenrollModal,
    acceptUnenrollClick
  } = useSyllabusProgrammePanel({
    programmeUID,
    goalUID: resolvedGoalUID,
    goalSlug: resolvedGoalSlug,
    programme: programmeDetails
  });

  const resolvedButtonVariant =
    buttonVariant === 'primary' ? 'brand' : buttonVariant;

  if (isLoading && !name) {
    return null;
  }

  if (!programmeUID || !name) {
    return null;
  }

  const scheduleLabel = getDateStringInTestSeriesHeader(startsAt, endsAt);
  const typeLabel = PROGRAMME_LABEL_MAPPING[programmeSubtype] || '';

  return (
    <>
      <Panel>
        <InnerColumn>
          <TopSection>
            <HeaderBlock>
              {typeLabel ? (
                <TypeLabel variant="p3" as="p">
                  {typeLabel}
                </TypeLabel>
              ) : null}
              <Title as="h2" variant="h4">
                {name}
              </Title>
            </HeaderBlock>
            <DetailsBlock>
              {authors?.length > 0 ? (
                <DetailRow>
                  <EducatorIconCircle>
                    <SubscriptionBenefits width={28} height={28} />
                  </EducatorIconCircle>
                  <DetailText>
                    <DetailLabel variant="p3" as="p">
                      {pluralize(authors.length, 'Educator', 's', true)}
                    </DetailLabel>
                    <DetailValue variant="p3" as="p">
                      {getEducatorsString(authors)}
                    </DetailValue>
                  </DetailText>
                </DetailRow>
              ) : null}
              {startsAt ? (
                <DetailRow $centerIcon>
                  <ScheduleIconWrap>
                    <SessionCancelled width={48} height={48} />
                  </ScheduleIconWrap>
                  <DetailText>
                    <DetailLabel variant="p3" as="p">
                      Test schedule
                    </DetailLabel>
                    <DetailValue variant="p3" as="p">
                      {scheduleLabel}
                    </DetailValue>
                  </DetailText>
                </DetailRow>
              ) : null}
            </DetailsBlock>
          </TopSection>
          {showPrimeTrialCta ? (
            <PrimeActionButton
              type="button"
              onClick={
                isEnrollStateValidating || isCtaLoading ? noOp : handleCtaClick
              }
              disabled={isEnrollStateValidating || isCtaLoading}
            >
              {isEnrollStateValidating || isCtaLoading ? (
                <BounceLoader />
              ) : (
                buttonLabel
              )}
            </PrimeActionButton>
          ) : (
            <ActionButton
              size="large"
              variant={
                isEnrollStateValidating ? 'secondary' : resolvedButtonVariant
              }
              label={isEnrollStateValidating ? <BounceLoader /> : buttonLabel}
              onClick={isEnrollStateValidating ? noOp : handleCtaClick}
              disabled={isEnrollStateValidating}
            />
          )}
        </InnerColumn>
      </Panel>
      <Dialog
        onClose={onCloseUnenrollModal}
        dialogState={unEnrollModalOpen}
        heading="Are you sure you want to unenroll?"
        message="This test-series will be removed from your schedule and you will stop receiving updates related to this course"
        acceptLabel="Unenroll"
        rejectLabel="Cancel"
        acceptOnClick={acceptUnenrollClick}
        rejectOnClick={onCloseUnenrollModal}
      />
    </>
  );
};

export default SyllabusPracticePanel;

SyllabusPracticePanel.propTypes = {
  programmeUID: PropTypes.string.isRequired,
  goalUID: PropTypes.string,
  goalSlug: PropTypes.string
};

SyllabusPracticePanel.defaultProps = {
  goalUID: '',
  goalSlug: ''
};
